import { observer, inject } from 'mobx-react';
import * as React from 'react';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import { Button, Card, Checkbox, Icon, Intent, Label, Spinner, TextArea, Dialog } from '@blueprintjs/core';
import { TLCode } from '../../common/enums';
import DutySelection from './DutySelection';
import styled from '@emotion/styled';
import { Pharmacy } from '../../store/models/Pharmacy';
import { Instance } from 'mobx-state-tree';
import { PharmacyDetails, PharmacyDetails as PharmacyDetailsEx } from '../../store/models/PharmacyDetails';
import { IconNames } from '@blueprintjs/icons';
import { DutyAssignment } from '../../store/models/DutyAssignment';
import { SystemStatus } from '../../store/models/SystemStatus';
import PharmacySelection from '../common/dropdownSelection/PharmacySelection';
import LeaveRouteDirtyWrapper from '../common/LeaveRouteDirtyWrapper';
import TranslatedMessage from '../common/translation/TranslatedMessage';
import moment from 'moment';
import { AuthenticationStore, PharmacyStore } from '../../store';
import { ConfirmationDialog } from '../common/dialog/ConfirmationDialog';
import { isEqual } from 'lodash';
import { DutyExchangeItem } from '../../store/models';
import { RadioGroup, SelectBox } from 'devextreme-react';
import DateInput from '../common/input/DateInput';
import { AllDaysOfWeek, DayOfWeek } from '../../common/enums/DateAndTime';
import { TranslateDayOfWeek } from '../../common/date/dateTranslationUtils';

export enum ExchangeSelectionType {
  ByPharmacy,
  ByDate,
  ByDayOfWeek,
}

interface NewExchangeTabProps extends WithTranslateProps {
  duties?: Array<Instance<typeof DutyAssignment>>;
  neighborDuties?: Array<Instance<typeof DutyAssignment>>;
  onExchangeRequesterSelected: (value) => void;
  onExchangeNeighborSelected: (value) => void;
  onCheck: () => void;
  onSave: () => void;
  onCancelCheck: () => void;
  onBypassCheck: () => void;
  pharmacyList: Array<Instance<typeof Pharmacy>>;
  pharmacyNeighboringList: Array<Instance<typeof PharmacyDetailsEx>>;
  pharmacyRequesterSelected?: Instance<typeof PharmacyDetails>;
  pharmacyNeighborSelected?: Instance<typeof PharmacyDetails>;
  onRequesterDutiesSelected: (value) => void;
  onNeighborDutiesSelected: (value) => void;
  isInConflict: (dutyAssignment, targetPharmacy) => boolean;
  hasAlreadyDutyOn: (dutyAssignment, targetPharmacy) => boolean;
  checkTooCloseDuties: (dutyAssignment, targetPharmacy) => boolean;
  checkTooCloseToRequester: (dutyAssignment) => any[];
  checkTooCloseToRecipient: (dutyAssignment) => any[];
  getPharmacyById: (id) => any;
  pharmacyUser: boolean;
  systemStatus?: Array<Instance<typeof SystemStatus>>;
  requesterDutiesSelected: number[];
  neighborDutiesSelected: number[];
  withAgreement?: boolean;
  withoutSubstitution?: boolean;
  comment?: string;
  onWithoutSubstitutionChange: () => void;
  onWithAgreementChange: () => void;
  onCommentChange: (value) => void;
  requestorDutiesIsLoading?: boolean;
  neighborDutiesIsLoading?: boolean;
  exchangeIsSaving?: boolean;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
  PharmacyStore?: Instance<typeof PharmacyStore>;
  onCheckResult: string[];
  lastExchangeItems?: Array<Instance<typeof DutyExchangeItem>>;
  exchangeTypes: Array<ExchangeSelectionType>;
  selectedExchangeType: ExchangeSelectionType;
  onSelectedExchangeTypeChange: (value: ExchangeSelectionType) => void;
  selectedDate?: Date;
  selectedDayOfWeek?: DayOfWeek;
  onDateChange: (date: Date) => void;
  onDayOfWeekChange: (dayOfWeek: DayOfWeek) => void;
}

interface NewExchangeTabState {
  notification: string;
  showWithoutSubstitution: boolean;
  isOpen: boolean;
  closeDialog: boolean;
  exchangeConfirmationDialogOpen: boolean;
}

@inject('AuthenticationStore', 'PharmacyStore')
@observer
class NewExchangeTab extends React.Component<NewExchangeTabProps, NewExchangeTabState> {
  state = {
    notification: '',
    showWithoutSubstitution: true,
    isOpen: false,
    closeDialog: false,
    exchangeConfirmationDialogOpen: false,
  } as NewExchangeTabState;

  componentDidUpdate(prevProps: Readonly<NewExchangeTabProps>): void {
    if (!isEqual(prevProps.lastExchangeItems, this.props.lastExchangeItems)) {
      this.setState({
        exchangeConfirmationDialogOpen: true,
      });
    }
  }

  getIsDirty = () => {
    const { requesterDutiesSelected, neighborDutiesSelected } = this.props;

    return requesterDutiesSelected.length > 0 || neighborDutiesSelected.length > 0;
  };

  somethingToSave = () => {
    const {
      requesterDutiesSelected,
      neighborDutiesSelected,
      withoutSubstitution,
      withAgreement,
      pharmacyUser,
    } = this.props;
    const ready =
      (!pharmacyUser || withAgreement) &&
      (requesterDutiesSelected.length > 0 || neighborDutiesSelected.length > 0) &&
      (!pharmacyUser || (neighborDutiesSelected.length > 0 || withoutSubstitution));
    return ready;
  };

  recipientDutySelected = (data) => {
    if (this.props.withoutSubstitution) {
      this.props.onWithoutSubstitutionChange();
    }
    this.setState({ showWithoutSubstitution: data.length == 0 });
    this.props.onNeighborDutiesSelected(data);
  };

  isRequesterInConflict = (dutyAssignmentId) => {
    if (this.props.pharmacyNeighborSelected)
      return this.props.isInConflict(dutyAssignmentId, this.props.pharmacyNeighborSelected!.pharmacyId);
    return false;
  };

  hasRequesterAlreadyDutyOn = (dutyAssignmentId) => {
    if (this.props.pharmacyNeighborSelected)
      return this.props.hasAlreadyDutyOn(dutyAssignmentId, this.props.pharmacyNeighborSelected!.pharmacyId);
    return false;
  };

  checkDutiesTooCloseToRequester = async (dutyAssignmentId) => {
    if (this.props.pharmacyRequesterSelected) return await this.props.checkTooCloseToRequester(dutyAssignmentId);
    return [];
  };

  checkDutiesTooCloseToRecipient = async (dutyAssignmentId) => {
    if (this.props.pharmacyNeighborSelected) return await this.props.checkTooCloseToRecipient(dutyAssignmentId);
    return [];
  };

  isRecipientInConflict = (dutyAssignmentId) => {
    if (this.props.pharmacyRequesterSelected)
      return this.props.isInConflict(dutyAssignmentId, this.props.pharmacyRequesterSelected!.pharmacyId);
    return false;
  };

  hasRecipientAlreadyDutyOn = (dutyAssignmentId) => {
    if (this.props.pharmacyRequesterSelected)
      return this.props.hasAlreadyDutyOn(dutyAssignmentId, this.props.pharmacyRequesterSelected!.pharmacyId);
    return false;
  };

  handleCancelOnError = () => {
    this.setState({ isOpen: false });
  };

  closeExchangeConfirmationDialog = () => {
    this.setState({ exchangeConfirmationDialogOpen: false });
  };

  getExchangeConfirmationToDisplay = (): JSX.Element => {
    const { translate, lastExchangeItems } = this.props;
    if (lastExchangeItems === undefined || lastExchangeItems.length === 0) return <></>;
    return (
      <div>
        <span>{translate(TLCode.EXCHANGE_Confirmation_Message)}</span>
        <br />
        <ul>
          {(() => {
            const groupedByDate = lastExchangeItems.reduce(
              (acc, element) => {
                const date = moment(element.dutyAssignment.duty!.dutyDate.date).format('ddd DD/MM/YYYY');
                if (!acc[date]) acc[date] = [];
                acc[date].push(element);
                return acc;
              },
              {} as Record<string, typeof lastExchangeItems>,
            );

            return Object.entries(groupedByDate).map(([date, elements]) => {
              let dayNight;
              if (elements.length > 1) {
                dayNight = translate(TLCode.EXCHANGE_Confirmation_Message_Detail_Day_Night);
              } else {
                const type = elements[0].dutyAssignment.duty!.dutyType!.type;
                dayNight =
                  type === 'DAY'
                    ? translate(TLCode.EXCHANGE_Confirmation_Message_Detail_Day)
                    : type === 'NIGHT'
                    ? translate(TLCode.EXCHANGE_Confirmation_Message_Detail_Night)
                    : translate(TLCode.EXCHANGE_Confirmation_Message_Detail_Day_Night);
              }
              const pharmacy = elements[0].recipient.name;
              return (
                <li key={date}>
                  {translate(TLCode.EXCHANGE_Confirmation_Message_Detail, {
                    ph: pharmacy,
                    dayNight,
                    date,
                  })}
                </li>
              );
            });
          })()}
        </ul>
        <span>{translate(TLCode.EXCHANGE_Confirmation_Message_Mail)}</span>
      </div>
    );
  };

  rightSideSelectorToRender = (): JSX.Element | null => {
    if (this.props.selectedExchangeType === ExchangeSelectionType.ByPharmacy) {
      return (
        <PharmacySelection
          pharmacyList={this.props.pharmacyNeighboringList.filter(
            (p) => this.props.pharmacyRequesterSelected!.level > 0 && p.level > 0,
          )}
          onChange={this.props.onExchangeNeighborSelected}
          pharmacySelected={this.props.pharmacyNeighborSelected}
          showDetails={true}
          showLevel={false}
          showDistance={true}
          showZipCode={true}
          colored={false}
          showPharmacyDetailsLevelAndGroup={this.props.AuthenticationStore!.isSecretariat}
        />
      );
    }
    if (this.props.selectedExchangeType === ExchangeSelectionType.ByDate) {
      return (
        <DateInput value={this.props.selectedDate} onChange={this.props.onDateChange} minDate={moment().toDate()} />
      );
    }
    if (this.props.selectedExchangeType === ExchangeSelectionType.ByDayOfWeek) {
      return (
        <SelectBox
          items={AllDaysOfWeek}
          selectedItem={this.props.selectedDayOfWeek}
          onValueChange={(value: any) => {
            this.props.onDayOfWeekChange(value);
          }}
          displayExpr={(item) => TranslateDayOfWeek(item, this.props.translate)}
        />
      );
    }
    return null;
  };

  private sortedDuties = () => {
    if (this.props.selectedExchangeType === ExchangeSelectionType.ByPharmacy) return this.props.neighborDuties!;

    return this.props
      .neighborDuties!.filter(
        (a) => this.props.pharmacyNeighboringList.find((ph) => ph.pharmacyId === a.pharmacyId) !== undefined,
      )
      .sort((a, b) => {
        const pharmacyADistance = this.props.pharmacyNeighboringList.find((ph) => ph.pharmacyId === a.pharmacyId)!
          .distance!;
        const pharmacyBDistance = this.props.pharmacyNeighboringList.find((ph) => ph.pharmacyId === b.pharmacyId)!
          .distance!;
        return pharmacyADistance > pharmacyBDistance ? 1 : pharmacyADistance < pharmacyBDistance ? -1 : 0;
      });
  };

  public render() {
    const {
      duties,
      neighborDuties,
      translate,
      onExchangeRequesterSelected,
      onSave,
      pharmacyList,
      pharmacyRequesterSelected,
      pharmacyNeighborSelected,
      getPharmacyById,
      pharmacyUser,
      systemStatus,
      neighborDutiesSelected,
      requesterDutiesSelected,
      requestorDutiesIsLoading,
      neighborDutiesIsLoading,
      exchangeIsSaving,
      onCheckResult,
      selectedDate,
      selectedDayOfWeek,
    } = this.props;
    const dirty = this.getIsDirty();
    const somethingToSave = this.somethingToSave();
    const disableSaveButton =
      !somethingToSave ||
      (this.state.showWithoutSubstitution && !this.props.withoutSubstitution) ||
      !this.props.withAgreement;
    const canBypass =
      this.props.AuthenticationStore!.isSecretariat ||
      this.props.AuthenticationStore!.isAdmin ||
      this.props.AuthenticationStore!.isSuperAdmin;

    return (
      <Container>
        <LeaveRouteDirtyWrapper blockChange={dirty} />
        {exchangeIsSaving ? (
          <SpinnerContainer>
            <Spinner intent={Intent.PRIMARY} size={Spinner.SIZE_STANDARD} />
          </SpinnerContainer>
        ) : (
          <>
            <SelectionContainer>
              <ConfirmationDialog
                isOpen={this.state.exchangeConfirmationDialogOpen}
                onContinue={this.closeExchangeConfirmationDialog}
                title={translate(TLCode.EXCHANGE_Confirmation_Title)}
                messageContent={this.getExchangeConfirmationToDisplay()}
                confirmText={translate(TLCode.EXCHANGE_Confirmation_Button)}
                cancelText={''}
              />
              <Dialog
                isOpen={onCheckResult.length < 0 && !this.state.closeDialog}
                isCloseButtonShown={true}
                onClose={() => {
                  this.setState({ closeDialog: true });
                  this.props.onCancelCheck();
                }}
                title={'Echange rejeté'}
                style={{ marginLeft: '10px', marginRight: '10px', width: '800px' }}
              >
                <DivColumn style={{ marginLeft: '10px' }}>
                  <br />
                  <p>
                    Ce transfert ne peut pas être enregistré. Veuillez essayer un autre transfert ou contacter votre
                    secrétariat de garde.
                  </p>
                  Causes du rejet:{' '}
                  <ul>
                    {this.props.onCheckResult.map((item) => {
                      const itemParts = item.split(' ');
                      const date = moment(itemParts[0]).format('ddd DD/MM/YYYY');
                      const type = itemParts[1] === 'DAY' ? 'jour' : 'nuit';
                      const sender = itemParts[2];
                      const recipient = itemParts[6];
                      const recipientPh = this.props.PharmacyStore!.dataList.filter(
                        (ph) => String(ph.apbNumber) === recipient,
                      );
                      const dist = Number(itemParts[7]) / 1000;
                      const minDist = Number(itemParts[9]) / 1000;
                      return (
                        <li>
                          {date} de {type} - ph. {sender} trop proche de ph. {recipient}
                          {recipientPh.length > 0 ? ' ' + recipientPh[0].name : ''} à {dist.toFixed(1)}km (min{' '}
                          {minDist.toFixed(1)}km)
                        </li>
                      );
                    })}
                  </ul>
                </DivColumn>
                <DivLine>
                  <DivLeftColumn />
                  <DivRightColumn style={{ width: '25%' }}>
                    <DivLine style={{ justifyContent: 'right' }}>
                      <Button
                        intent="primary"
                        text="Abandonner"
                        onClick={() => {
                          this.setState({ closeDialog: true });
                          this.props.onCancelCheck();
                        }}
                      />
                      &nbsp;&nbsp;
                      {canBypass && (
                        <Button
                          intent="danger"
                          text="Maintenir"
                          onClick={() => {
                            this.setState({ closeDialog: true });
                            this.props.onBypassCheck();
                          }}
                        />
                      )}
                      &nbsp;&nbsp;
                    </DivLine>
                  </DivRightColumn>
                </DivLine>
              </Dialog>
              <PharmacyContainer>
                <Card
                  title={translate(TLCode.EXCHANGE_PHARMACY_REQUESTER_CARD_TITLE)}
                  style={{ marginBottom: 10, minHeight: 190 }}
                >
                  <PharmacySelection
                    pharmacyList={pharmacyList}
                    onChange={onExchangeRequesterSelected}
                    pharmacySelected={pharmacyRequesterSelected}
                    getPharmacyById={getPharmacyById}
                    disabled={pharmacyUser}
                    showDetails={true}
                    showZipCode={true}
                    colored={false}
                    showPharmacyDetailsLevelAndGroup={this.props.AuthenticationStore!.isSecretariat}
                  />
                </Card>
                {pharmacyRequesterSelected || requestorDutiesIsLoading ? (
                  <Card
                    title={translate(TLCode.EXCHANGE_DUTIES_CARD_TITLE)}
                    style={{ marginBottom: 10, height: '300px', overflowY: 'auto' }}
                  >
                    {requestorDutiesIsLoading ? (
                      <SpinnerContainer>
                        <Spinner intent={Intent.PRIMARY} size={Spinner.SIZE_STANDARD} />
                      </SpinnerContainer>
                    ) : pharmacyNeighborSelected || selectedDate || selectedDayOfWeek !== undefined ? (
                      <DutySelection
                        dutyList={duties!}
                        onChange={(data) => {
                          this.setState({ closeDialog: false });
                          var res = this.props.onRequesterDutiesSelected(data);
                        }}
                        systemStatus={systemStatus}
                        pharmacyUser={pharmacyUser}
                        selectedDuties={requesterDutiesSelected}
                        isInConflict={this.isRequesterInConflict}
                        hasAlreadyDutyOn={this.hasRequesterAlreadyDutyOn}
                        checkTooCloseDuties={this.checkDutiesTooCloseToRecipient}
                        sortByDate={true}
                      />
                    ) : (
                      <div>Veuiller d'abord choisir avec quelle pharmacie, date ou jour échanger</div>
                    )}
                  </Card>
                ) : null}
              </PharmacyContainer>
              <IconContainer>
                <div>
                  <Icon icon={IconNames.DOUBLE_CHEVRON_LEFT} iconSize={50} />
                  <Icon icon={IconNames.DOUBLE_CHEVRON_RIGHT} iconSize={50} />
                </div>
              </IconContainer>
              <PharmacyContainer>
                {pharmacyRequesterSelected ? (
                  <div>
                    <Card
                      title={translate(TLCode.EXCHANGE_PHARMACY_OTHER_CARD_TITLE)}
                      style={{ marginBottom: 10, minHeight: 190 }}
                    >
                      <DivLine>
                        <DivLeftColumn>{this.rightSideSelectorToRender()}</DivLeftColumn>
                        <DivRightColumn>
                          <div style={{ marginBottom: 8 }}>
                            <RadioGroup
                              items={this.props.exchangeTypes}
                              displayExpr={(item: ExchangeSelectionType) => {
                                const TLCodeToApply =
                                  item === ExchangeSelectionType.ByPharmacy
                                    ? TLCode.EXCHANGE_SelectionType_ByPharmacy
                                    : item === ExchangeSelectionType.ByDate
                                    ? TLCode.EXCHANGE_SelectionType_ByDate
                                    : TLCode.EXCHANGE_SelectionType_ByDayOfWeek;
                                return translate(TLCodeToApply);
                              }}
                              value={this.props.selectedExchangeType}
                              onValueChange={this.props.onSelectedExchangeTypeChange}
                            />
                          </div>
                          <div>
                            <p style={{ color: 'red', marginBottom: 8 }}>
                              <TranslatedMessage id={TLCode.EXCHANGE_DUTIES_AsTheCrowFlies} />
                            </p>
                            <p style={{ marginBottom: 4 }}>
                              <TranslatedMessage id={TLCode.EXCHANGE_DUTIES_NoAcceptablePharmacy} />
                            </p>
                          </div>
                        </DivRightColumn>
                      </DivLine>
                    </Card>
                    {pharmacyNeighborSelected ||
                    selectedDate ||
                    selectedDayOfWeek !== undefined ||
                    neighborDutiesIsLoading ? (
                      <Card
                        title={translate(TLCode.EXCHANGE_DUTIES_CARD_TITLE)}
                        style={{ marginBottom: 10, height: '300px', overflowY: 'auto' }}
                      >
                        {neighborDutiesIsLoading ? (
                          <SpinnerContainer>
                            <Spinner intent={Intent.PRIMARY} size={Spinner.SIZE_STANDARD} />
                          </SpinnerContainer>
                        ) : (
                          <DutySelection
                            dutyList={this.sortedDuties()}
                            onChange={(data) => {
                              this.setState({ closeDialog: false });
                              var res = this.recipientDutySelected(data);
                            }}
                            systemStatus={systemStatus}
                            pharmacyUser={pharmacyUser}
                            disable={this.props.withoutSubstitution}
                            selectedDuties={neighborDutiesSelected}
                            isInConflict={this.isRecipientInConflict}
                            hasAlreadyDutyOn={this.hasRecipientAlreadyDutyOn}
                            checkTooCloseDuties={this.checkDutiesTooCloseToRequester}
                            showPharmacyName={selectedDate !== undefined || selectedDayOfWeek !== undefined}
                            sortBydate={pharmacyNeighborSelected}
                          />
                        )}
                      </Card>
                    ) : null}
                  </div>
                ) : null}
              </PharmacyContainer>
            </SelectionContainer>
            <FormContainer>
              <AgreementDiv>
                {pharmacyUser && (
                  <DivLine style={{ width: '600px' }}>
                    <Checkbox onChange={this.props.onWithAgreementChange} checked={this.props.withAgreement}>
                      {translate(TLCode.EXCHANGE_WithAgreementMessage)}
                      {!this.props.withAgreement && (
                        <span style={{ color: 'red', fontWeight: 'bold' }}>
                          &nbsp;&nbsp;Champ obligatoire&nbsp;&nbsp;
                        </span>
                      )}
                    </Checkbox>
                  </DivLine>
                )}
              </AgreementDiv>
              <NoReturnDiv>
                {pharmacyUser && this.state.showWithoutSubstitution && (
                  <Checkbox onChange={this.props.onWithoutSubstitutionChange} checked={this.props.withoutSubstitution}>
                    {translate(TLCode.EXCHANGE_WithoutSubstitutionMessage)}
                  </Checkbox>
                )}
              </NoReturnDiv>
              <br />
              {pharmacyUser && (
                <RemarkDiv>
                  <Label style={{ marginRight: '10px' }}>{translate(TLCode.EXCHANGE_Remark)}</Label>
                  <TextArea
                    fill={true}
                    value={this.props.comment}
                    onChange={(value) => this.props.onCommentChange(value.target.value)}
                  />
                </RemarkDiv>
              )}
            </FormContainer>
            <ButtonsContainer>
              {!somethingToSave && <p style={{ color: 'red', fontWeight: 'bold' }}>Formulaire incomplet&nbsp;&nbsp;</p>}
              <Button
                disabled={!somethingToSave}
                text={translate(TLCode.EXCHANGE_Save)}
                intent={Intent.PRIMARY}
                onClick={() => {
                  const res = onSave();
                }}
              />
            </ButtonsContainer>
          </>
        )}
      </Container>
    );
  }
}

export default withTranslate(NewExchangeTab);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const SelectionContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: 10px;
`;

const PharmacyContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  flex: 0.45;
`;

const IconContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  flex: 0.1;
  vertical-align: middle;
  justify-content: center;
  align-items: center;
`;

const RemarkDiv = styled.div`
  display: flex;
  flex-direction: row;
  width: 60%;
`;

const NoReturnDiv = styled.div`
  margin-top: 10px;
`;

const AgreementDiv = styled.div`
  margin-top: 10px;
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const SpinnerContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const DivColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const DivLeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 10px;
  width: 50%;
`;

const DivRightColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
`;
